import * as z from "zod";

export const REVIEW_STAGES = [
  "setting_up",
  "reviewing",
  "checking_findings",
] as const;

export type ReviewStage = (typeof REVIEW_STAGES)[number];

export function isReviewStage(value: unknown): value is ReviewStage {
  return REVIEW_STAGES.includes(value as ReviewStage);
}

export const REVIEW_STAGE_LABELS: Record<ReviewStage, string> = {
  setting_up: "Setting up",
  reviewing: "Reviewing changes",
  checking_findings: "Checking findings",
};

export const REVIEW_INTERRUPTIONS = [
  "superseded",
  "cancelled",
  "stopped",
] as const;

export type ReviewInterruption = (typeof REVIEW_INTERRUPTIONS)[number];

export function isReviewInterruption(
  value: unknown,
): value is ReviewInterruption {
  return REVIEW_INTERRUPTIONS.includes(value as ReviewInterruption);
}

export const ReviewRunSnapshot = z.object({
  child_session_id: z.string(),
  repository: z.string().nullable(),
  pr_number: z.number().nullable(),
  pr_url: z.string().nullable(),
  /** The pinned PR head this review round evaluates. */
  head_sha: z.string().nullable().default(null),
  started_at: z.string(),
  stage: z.enum(REVIEW_STAGES).catch("setting_up"),
  /** The child's claimed user message; distinguishes a stale snapshot of a settled turn from the child's next turn. */
  turn_id: z.string().nullable(),
  /** That message's index: turn ids are random UUIDs, so this is what orders a child's turns. */
  turn_idx: z.number().nullable().default(null),
  /** The frame sent when the child's turn ends; `active_review_runs` carries it only for a failed webhook round. */
  settled: z.boolean().default(false),
  failure_kind: z.string().nullable().optional(),
  /** On a settle frame when the turn ended without a report, why the turn was interrupted. */
  interrupted: z.enum(REVIEW_INTERRUPTIONS).nullable().default(null),
  /** On a settle frame: the turn ended in error, so the card can offer Retry before the activity poll notices. */
  failed: z.boolean().default(false),
});

export type ReviewRunSnapshot = z.infer<typeof ReviewRunSnapshot>;

/** A frame as held client-side; `receivedAt` (epoch ms) orders a settle frame against status polls. */
export type ReviewRunFrame = ReviewRunSnapshot & { receivedAt?: number };

export type ReviewStatusPhase =
  | ReviewStage
  | ReviewInterruption
  | "finished"
  | "failed";

/** A settled frame holds the card at its outcome until the report or a follow-up turn replaces it. */
export function reviewRunPhase(run: ReviewRunSnapshot): ReviewStatusPhase {
  if (!run.settled) return run.stage;
  if (run.interrupted) return run.interrupted;
  return run.failed ? "failed" : "finished";
}
